import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportingLeDataSessionVisibilityPath } from '~/consts/reportingLeDataApi';

import { json_headers, request } from '~/services/common';

import type { LeDataSessionMetadata, LeDataSessionResponse, LeDataSessionVisibility } from '~/types/leData';

import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

import { reportingLeDataSessionQueryKeys } from './useGetReportingLeDataSession';
import { reportingLeDataSharedSessionQueryKeys } from './useGetReportingLeDataSharedSession';

type SetSessionVisibilityInput = {
  sessionId: string;
  visibility: LeDataSessionVisibility;
};

export function useSetReportingLeDataSessionVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, visibility }: SetSessionVisibilityInput) => {
      const url = resolveReportingLeDataUrl(reportingLeDataSessionVisibilityPath(sessionId));
      return (await request(url, {
        method: 'POST',
        headers: json_headers,
        body: JSON.stringify({ visibility }),
      })) as LeDataSessionMetadata;
    },
    onSuccess: (data, { sessionId, visibility }) => {
      queryClient.setQueryData<LeDataSessionResponse>(reportingLeDataSessionQueryKeys.detail(sessionId), (previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          session: {
            sessionId,
            ...previous.session,
            visibility: data.visibility ?? visibility,
            sharedAt: data.sharedAt ?? null,
          },
        };
      });

      if ((data.visibility ?? visibility) === 'private') {
        queryClient.removeQueries({ queryKey: reportingLeDataSharedSessionQueryKeys.detail(sessionId) });
      }
    },
  });
}
